import { mmFetchWithResponse } from 'api/mmFetchWithResponse';

const OOS_LOG_EVENTS_PATH = 'api/warehouse/v1/oos-log/events';

/**
 * @param {unknown} value
 * @returns {number | undefined}
 */
function normalizePositiveInteger(value) {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    const n = Number(value);
    if (!Number.isInteger(n) || n <= 0) {
        return undefined;
    }
    return n;
}

/**
 * Normalizes a facility id to the positive integer required by `GET /warehouse/v1/oos-log/events`.
 *
 * @param {unknown} value
 * @returns {number | undefined}
 */
export function mapFacilityIdToApiParam(value) {
    return normalizePositiveInteger(value);
}

/**
 * @param {unknown} value
 * @returns {string | undefined}
 */
function normalizeIsoTimestamp(value) {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    const s = String(value).trim();
    return s || undefined;
}

/**
 * Builds URLSearchParams for `GET /warehouse/v1/oos-log/events`.
 *
 * @param {Object} [params={}]
 * @param {number | string} params.facilityId
 * @param {number} [params.page=1]
 * @param {number} [params.pageSize=50]
 * @param {number | string} [params.externalSku]
 * @param {string} [params.start]
 * @param {string} [params.end]
 * @returns {URLSearchParams}
 */
export function buildOosLogEventsSearchParams(params = {}) {
    const queryParams = new URLSearchParams();

    const facilityId = mapFacilityIdToApiParam(params.facilityId);
    if (facilityId !== undefined) {
        queryParams.set('facilityId', String(facilityId));
    }

    const page = normalizePositiveInteger(params.page) ?? 1;
    queryParams.set('page', String(page));

    const pageSize = normalizePositiveInteger(params.pageSize) ?? 50;
    queryParams.set('pageSize', String(pageSize));

    const externalSku = normalizePositiveInteger(params.externalSku);
    if (externalSku !== undefined) {
        queryParams.set('externalSku', String(externalSku));
    }

    const start = normalizeIsoTimestamp(params.start);
    const end = normalizeIsoTimestamp(params.end);
    if (start && end) {
        queryParams.set('start', start);
        queryParams.set('end', end);
    }

    return queryParams;
}

/**
 * Fetches OOS log events for a facility.
 *
 * @param {Object} [params={}]
 * @param {number | string} [params.facilityId]
 * @param {number} [params.page]
 * @param {number} [params.pageSize]
 * @param {number | string} [params.externalSku]
 * @param {string} [params.start]
 * @param {string} [params.end]
 * @param {Object} [options={}]
 * @returns {Promise<{status: boolean, msg?: string, data?: Object, statusCode?: number | null}>}
 */
export async function searchOosLogEvents(params = {}, options = {}) {
    const facilityId = mapFacilityIdToApiParam(params.facilityId);
    if (facilityId === undefined) {
        return Promise.resolve({
            status: false,
            msg: 'facilityId is required',
            data: {},
            statusCode: null,
        });
    }

    const url = `${OOS_LOG_EVENTS_PATH}?${buildOosLogEventsSearchParams(params).toString()}`;
    return mmFetchWithResponse(url, options);
}
